import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { getFirstAvailableRoute } from '@/router'
import type { VForm } from 'vuetify/components'

interface LoginFormData {
  email: string
  password: string
  rememberMe: boolean
}

type ValidatableField = 'email' | 'password'

type ValidationRule = (value: string) => boolean | string

export function useLogin() {
  const router = useRouter()
  const authStore = useAuthStore()

  const formRef = ref<VForm | null>(null)

  const formData = ref<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
  })

  const isFormValid = ref(false)

  const isLoading = computed(() => authStore.isLoading)

  const showPassword = ref(false)

  const errorMessage = computed(() => authStore.error)

  const emailRules: ValidationRule[] = [
    (v: string) => !!v || 'El correo electrónico es requerido',
    (v: string) => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailPattern.test(v) || 'Ingresa un correo electrónico válido'
    },
  ]

  const passwordRules: ValidationRule[] = [
    (v: string) => !!v || 'La contraseña es requerida',
    (v: string) => (v && v.length >= 6) || 'La contraseña debe tener al menos 6 caracteres',
  ]

  const togglePassword = (): void => {
    showPassword.value = !showPassword.value
  }

  const validateField = async (field: ValidatableField): Promise<void> => {
    if (formRef.value) {
      await formRef.value.validate()
    }
  }

  const clearError = (): void => {
    if (authStore.clearError) {
      authStore.clearError()
    }
  }

  const handleForgotPassword = (): void => {
    // TODO: Implementar recuperación de contraseña
    // router.push({ name: 'ForgotPassword' })
    console.log('Recuperar contraseña')
  }

  const handleSubmit = async (): Promise<void> => {
    if (formRef.value) {
      const { valid } = await formRef.value.validate()
      if (!valid) {
        return
      }
    }
    clearError()

    try {
      await authStore.login({
        email: formData.value.email.trim(),
        password: formData.value.password,
      })

      const redirectPath = router.currentRoute.value.query.redirect as string
      const redirectRoute = redirectPath || getFirstAvailableRoute(authStore.userRole)

      await router.push(redirectRoute)
    } catch {
      // El error ya está manejado en el store (authStore.error)
      // Solo se muestra el mensaje de error en el componente
    }
  }

  return {
    // Referencias
    formRef,
    formData,

    // Estados
    isFormValid,
    isLoading,
    showPassword,
    errorMessage,

    // Reglas de validación
    emailRules,
    passwordRules,

    // Métodos de interacción
    handleSubmit,
    togglePassword,
    validateField,
    clearError,
    handleForgotPassword,
  }
}
